/* eslint-disable @next/next/no-img-element */
import { LineDragIndicator } from '@qr-front/icons/line-drag-indicator'
import { LineTrashAlt } from '@qr-front/icons/line-trash-alt'
import { cn } from '@qr-front/ui/utils'
import {
	DESCRIPTION_MAX_LENGTH,
	URL_MAX_LENGTH,
} from 'constants/validationConstants'
import { RhcField } from 'features/generator/components/RhcField'
import { qrTypeAtom } from 'features/generator/generatorState'
import { QR_TYPE } from 'features/generator/generatorTypes'
import {
	SOCIAL_PLATFORM_ICONS,
	SOCIAL_PLATFORM_LIST,
} from 'features/generator/utils/socialPlatformsConfig'
import { useAtom, useAtomValue } from 'jotai'
import { Reorder, useDragControls } from 'motion/react'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { maxLength } from 'utils/inputChangeUtils'

import { InputButton } from '../InputButton'
import { socialLinkListAtom } from './SocialLinksForm'
import { TSocialLinksFormFields } from './socialLinksFormSchema'

export const SocialLinkField = ({
	index,
	isDraggable,
	onDragEnd,
}: {
	index: number
	isDraggable: boolean
	onDragEnd: () => void
}) => {
	const t = useTranslations()
	const dragControls = useDragControls()
	const qrType = useAtomValue(qrTypeAtom)
	const form = useFormContext<TSocialLinksFormFields>()
	const {
		formState: { errors },
		trigger,
	} = form

	const [socialLinkList, setSocialLinkList] = useAtom(socialLinkListAtom)
	const hasCustomSocialLinks =
		qrType === QR_TYPE.SocialMedia || qrType === QR_TYPE.VCard

	const platformId = useMemo(
		() => socialLinkList[index]?.platformId,
		[index, socialLinkList],
	)

	const platformDetails = useMemo(
		() => SOCIAL_PLATFORM_LIST.find(({ id }) => id === platformId),
		[platformId],
	)

	const label = useMemo(() => platformDetails?.name, [platformDetails?.name])
	const customPlatformLabel = useMemo(
		() => socialLinkList[index]?.name,
		[index, socialLinkList],
	)

	const Icon = useMemo(() => platformDetails?.Icon, [platformDetails?.Icon])

	const handleClickDelete = () => {
		const newList = socialLinkList.filter((_, i) => i !== index)
		setSocialLinkList(newList)
		trigger('socialLinks')
	}

	return (
		<Reorder.Item
			as="li"
			value={socialLinkList[index]}
			dragListener={false}
			dragControls={dragControls}
			onDragEnd={onDragEnd}
			whileDrag={{
				scale: 1.02,
				boxShadow: '0px 8px 24px rgba(63, 72, 103, 0.16)',
			}}
			className={cn(
				'relative flex w-full flex-col items-start justify-between gap-4 rounded-10 bg-light-grey-70 p-4 tablet:flex-row',
				hasCustomSocialLinks ? 'flex-wrap' : null,
			)}
		>
			<div className="flex items-center gap-2">
				{Icon && <Icon className="shrink-0" />}
				{socialLinkList[index]?.logo?.publicId?.length ? (
					<div className="flex size-10 items-center justify-center">
						<img
							src={`${socialLinkList[index]?.logo?.bucketRootUrl}/${socialLinkList[index]?.logo?.publicId}`}
							alt="Social image"
							width={33}
							height={33}
							className="shrink-0 rounded-full object-contain"
						/>
					</div>
				) : null}
				{socialLinkList[index]?.logo?.src?.endsWith(
					'custom-social.svg',
				) && <SOCIAL_PLATFORM_ICONS.Web className="shrink-0" />}
				<span className="type-body-medium font-semibold word-break-fix">
					{label ?? customPlatformLabel}
				</span>
			</div>
			<div
				className={cn(
					hasCustomSocialLinks
						? 'flex-col tablet:ml-auto desktop:flex-row'
						: 'flex-row tablet:flex-row',
					'flex w-full gap-2 tablet:w-1/2 desktop:w-auto',
				)}
			>
				<div
					className={cn(
						hasCustomSocialLinks
							? 'desktop:w-[14rem]'
							: 'desktop:w-[28.5rem]',
						'w-full grow desktop:grow-0',
					)}
				>
					<RhcField
						form={form}
						path={`socialLinks.${index}.url`}
						type="url"
						required
						value={socialLinkList[index]?.url || ''}
						setValue={(val) => {
							const newList = [...socialLinkList]
							newList[index]!.url = val ?? ''
							setSocialLinkList(newList)
						}}
						placeholder={t(
							'generator__content_form_section__social__input_placeholder',
						)}
						fieldName="social_link_url"
						fieldGroup="social_links"
						error={errors.socialLinks?.[index]?.url?.message}
						onChangeTransform={(event) =>
							maxLength(URL_MAX_LENGTH + 1)(event)
						}
					/>
				</div>
				<div className="flex gap-2">
					{hasCustomSocialLinks && (
						<RhcField
							form={form}
							path={`socialLinks.${index}.description`}
							type="text"
							value={socialLinkList[index]?.description || ''}
							setValue={(val) => {
								const newList = [...socialLinkList]
								newList[index]!.description = val ?? ''
								setSocialLinkList(newList)
							}}
							fieldName="social_link_description"
							fieldGroup="social_links"
							placeholder={t(
								'generator__content_form_section__social__input__description_placeholder',
							)}
							onChangeTransform={(event) =>
								maxLength(DESCRIPTION_MAX_LENGTH)(event)
							}
							className="grow"
						/>
					)}
					<InputButton
						icon={<LineTrashAlt className="size-5" />}
						onClick={handleClickDelete}
					/>
					{isDraggable && (
						<div
							aria-hidden
							data-testid="social-link-drag-handle"
							onPointerDown={(event) => dragControls.start(event)}
							className="flex h-12 shrink-0 cursor-grab touch-none select-none items-center text-dark-grey active:cursor-grabbing"
						>
							<LineDragIndicator className="size-5" />
						</div>
					)}
				</div>
			</div>
		</Reorder.Item>
	)
}
